import { reactive, watchEffect, onBeforeUnmount } from 'vue'

function useSizeObserver(element: any, targetElement?: any) {
  const size = reactive({
    width: 0,
    height: 0
  })

  let el: Element | null = null
  let resizeObserver: ResizeObserver | null = null

  function resolveObservedElement(value: any): Element | null {
    const candidate = targetElement ? value?.[targetElement] : value
    if (candidate instanceof Element) return candidate
    if (candidate?.$el instanceof Element) return candidate.$el
    return null
  }

  function cleanupObserver() {
    if (!resizeObserver) return
    resizeObserver.disconnect()
    resizeObserver = null
    el = null
  }

  watchEffect(() => {
    cleanupObserver()

    if (!element.value) return

    el = resolveObservedElement(element.value)
    if (!el) return

    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        size.width = entry.contentRect.width
        size.height = entry.contentRect.height
      }
    })
    resizeObserver.observe(el)

    if (el instanceof HTMLElement) {
      size.width = el.offsetWidth
      size.height = el.offsetHeight
    } else {
      const rect = el.getBoundingClientRect()
      size.width = rect.width
      size.height = rect.height
    }
  })

  onBeforeUnmount(() => {
    cleanupObserver()
  })

  return size
}

/**
 * 度量以connector连接的字符串在指定容器中分为多少行，每行的起始和结尾位置在哪里。
 * 上一行被截断的字符会自动挪动到下一行。

 */
function measureTextArray(
  texts: string[],
  connector: string,
  container: HTMLElement
): { head: number; tail: number; texts: string[] }[] {
  if (texts.length == 0) {
    return []
  }
  const style = window.getComputedStyle(container)
  const containerWidth = container.clientWidth
  const div = document.createElement('div')
  div.style.fontSize = style.fontSize
  div.style.boxSizing = style.boxSizing
  div.style.margin = style.margin
  div.style.padding = style.padding
  div.style.border = style.border
  div.style.visibility = 'hidden'
  div.style.whiteSpace = 'nowrap'
  div.style.position = 'absolute'
  div.style.top = '-9999px'
  div.style.left = '-9999px'
  document.body.appendChild(div)
  const lines: { head: number; tail: number; texts: string[] }[] = []
  let currentHeadPos = 0
  while (currentHeadPos < texts.length) {
    let needNewLine = false
    const line = {
      head: currentHeadPos,
      tail: 0,
      texts: []
    }
    for (let wordPos = currentHeadPos; wordPos < texts.length; wordPos++) {
      const subTexts = texts.slice(currentHeadPos, wordPos + 1)
      const text = subTexts.join(connector)
      div.innerText = text
      if (div.clientWidth > containerWidth) {
        line.tail = wordPos - 1
        //ignore single item which already exceeds max width
        if(currentHeadPos==wordPos){
          needNewLine = false
        }
        else {
          currentHeadPos = wordPos
          needNewLine = true
        }
        break
      }
    }
    if (!needNewLine) {
      line.tail = texts.length - 1
      currentHeadPos = texts.length
    }

    line.texts = texts.slice(line.head, line.tail + 1)
    lines.push(line)
  }
  document.body.removeChild(div)
  return lines
}
// 获取根元素的字体大小
function getRootFontSize() {
  return parseFloat(getComputedStyle(document.documentElement).fontSize)
}

// 将rem转换为px
function remToPx(rem) {
  const rootFontSize = getRootFontSize()
  return rem * rootFontSize
}

export { useSizeObserver, measureTextArray, remToPx }
